import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, CircularProgress, Tab } from "@mui/material";
import Basket from "assets/img/svg/basket";
import Pdf from "assets/img/svg/pdf";
import SendDocument from "assets/img/svg/sendDocument";
import PickerCompany from "components/BPPickerCompany";
import ActionDocumentModal from "components/shared/ActionDocumentModal";
import AddDocument from "components/shared/AddDocument";
import { displayMobileNavBtn } from "components/shared/BPMobileNavBtn";
import BPSignedDocumentsList from "components/shared/BPSignedDocumentsList";
import ButtonBase from "components/shared/ButtonBase";
import DeleteModal from "components/shared/DeleteModal";
import EmptyState from "components/shared/EmptyState";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import StatusChip from "components/shared/StatusChip";
import { StyledTabs } from "components/shared/StyledTabs";
import { documentStatusRules, formatShortDate, getDocumentRowStatus } from "helpers/beneficiaryDocuments";
import { formatLocaleDateString, getBPAvatarUrl, getBPCompanyName, getFileSize } from "helpers/utils";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsPWA } from "hooks/usePWAMode";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { IBeneficiaryDocumentType } from "store/beneficiaryDocumentTypesStore";
import { emptyCriteria } from "store/beneficiaryDocumentTypesStore";
import { getCompagnies } from "store/beneficiaryStore";
import type { IDocument, IExpectedDocument, IExpectedDocumentsByCompany } from "store/documentStore";
import {
    deleteDocumentBP,
    fetchDocumentBP,
    fetchDocumentsBP,
    fetchExpectedDocumentsBP,
    sendDocumentsBP,
} from "store/documentStore";
import type { AppDispatch, RootState } from "store/store";
import type { IBeneficiariesCompanies } from "store/types";

type TDocumentsTab = "expected" | "transmitted" | "signed";

const BPDocuments = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { beneficiarCompagnies } = useSelector((state: RootState) => state.beneficiary);
    const { documentList, documentListLoadStatus, expectedDocuments } = useSelector(
        (state: RootState) => state.documents,
    );
    const statusRules = useMemo(() => documentStatusRules(t), [t]);
    const { user } = useAuthenticator((context) => [context.user]);
    const userEmail = user.attributes ? user.attributes["email"] : "";
    const { isMobile } = useIsMobile();
    const isPWAMode = useIsPWA();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const initialActionModalState = { open: false, document: undefined };
    const [actionModal, setActionModal] = useState<{ open: boolean; document: IDocument | undefined }>(
        initialActionModalState,
    );
    const [documentToDelete, setDocumentToDelete] = useState<IDocument | undefined>();
    const [chosenCompanies, setChosenCompanies] = useState<IBeneficiariesCompanies[]>([]);
    const [defaultTypeId, setDefaultTypeId] = useState<number | null>(null);
    const [filterDocChosenCompanies, setFilteredDocChosenCompanies] = useState<IBeneficiariesCompanies[]>([]);
    const [chosenTab, setChosenTab] = useState<TDocumentsTab | null>(null);

    const filteredDocuments = useMemo(
        () =>
            documentList.filter((item: any) =>
                filterDocChosenCompanies.length > 0
                    ? filterDocChosenCompanies.some((comp) => comp.id == item.companyId)
                    : false,
            ),
        [documentList, filterDocChosenCompanies],
    );

    const handleShowDocumentDeleteModal = (document?: IDocument) => {
        if (document == undefined || document.id == undefined) return;
        setIsDeleteModalOpen(true);
        setDocumentToDelete(document);
    };

    const handleDeleteDocument = async (isDeleting = true) => {
        if (isDeleting) await dispatch(deleteDocumentBP({ documentId: documentToDelete?.id ?? -1 }));
        setIsDeleteModalOpen(false);
        setDocumentToDelete(undefined);
    };

    useEffect(() => {
        dispatch(fetchDocumentsBP({ companyId: undefined }));
        dispatch(fetchExpectedDocumentsBP());
        dispatch(getCompagnies());
    }, []);

    // The reminder email links to this section
    useEffect(() => {
        if (window.location.hash === "#expected" && expectedDocuments.length) {
            document.getElementById("expected")?.scrollIntoView({ behavior: "smooth" });
        }
    }, [expectedDocuments]);

    const hasExpectedDocuments = expectedDocuments.some((entry) => entry.expected.length > 0);
    // Expected documents load async: the default tab is only known once they are in
    const tab: TDocumentsTab =
        chosenTab === "expected" && !hasExpectedDocuments
            ? "transmitted"
            : chosenTab ?? (hasExpectedDocuments ? "expected" : "transmitted");

    const uploadCompanyId = chosenCompanies[0]?.id;
    /** Types the chosen company expects from the author, in the shape the upload modal works with (stable identity) */
    const uploadCompanyTypes = useMemo(
        (): IBeneficiaryDocumentType[] =>
            (expectedDocuments.find((entry) => entry.companyId === uploadCompanyId)?.expected ?? []).map(
                (expected) => ({
                    id: expected.typeId,
                    companyId: uploadCompanyId as number,
                    name: expected.typeName,
                    requiresValidity: expected.requiresValidity,
                    criteria: emptyCriteria,
                    isActive: true,
                    sortOrder: 0,
                    createdAt: "",
                    updatedAt: null,
                }),
            ),
        [expectedDocuments, uploadCompanyId],
    );
    const documentsOfUploadCompany = useMemo(
        () => documentList.filter((item: IDocument) => item.companyId == uploadCompanyId),
        [documentList, uploadCompanyId],
    );

    useEffect(() => {
        setChosenCompanies(beneficiarCompagnies.slice(0, 1));
        setFilteredDocChosenCompanies(beneficiarCompagnies);
    }, [beneficiarCompagnies]);

    const handleFileAdd = (file: { formData: FormData }) => {
        dispatch(
            sendDocumentsBP({
                formData: file.formData,
                companyId: `${chosenCompanies[0].id}`,
            }),
        ).then(() => dispatch(fetchExpectedDocumentsBP()));
    };

    /** "Déposer" on an expected document: the modal opens on that company and type */
    const onDepositExpected = (companyId: number, expected: IExpectedDocument) => {
        const company = beneficiarCompagnies.find((item) => item.id === companyId);
        if (company) setChosenCompanies([company]);
        setDefaultTypeId(expected.typeId);
        onOpenSendDocument();
    };

    const handleFileDownload = (file: any) => {
        dispatch(fetchDocumentBP(file)).then(() => {
            if (isMobile) {
                setTimeout(() => {
                    onCloseDocumentAction();
                }, 800);
            }
        });
    };

    const documentsTableColumns: LastElementTableColumns[] = [
        {
            id: "name",
            avatar: false,
            label: t("BeneficiarInfo.Name"),
            align: "left",
            sort: true,
            format: (value: any, id, row) => (
                <div className="mui-cell" onClick={() => handleFileDownload(row)}>
                    <Pdf />
                    {value}
                </div>
            ),
        },
        {
            id: "uploadedBy",
            label: t("BeneficiarInfo.UploadedBy"),
            align: "left",
            sort: true,
            format: (value: any) =>
                value == userEmail ? t("BeneficiarInfo.You") : t("BeneficiarInfo.YourPublisher") ?? "-",
        },
        {
            id: "companyId",
            label: t("BeneficiarInfo.Company"),
            align: "left",
            sort: true,
            format: (value: any, id, row) => (
                <div className="documents-table-company-logo">
                    <Avatar
                        variant="rounded"
                        sx={{ width: 20, height: 20 }}
                        src={getBPAvatarUrl(value, beneficiarCompagnies)}
                    />
                    {getBPCompanyName(value, beneficiarCompagnies)}
                </div>
            ),
        },
        {
            id: "status",
            label: t("BeneficiaryDocuments.Portal.Status"),
            align: "left",
            format: (_value: any, id, row) => <DocumentStatusChip document={row} documents={documentList} />,
        },
        {
            id: "size",
            label: t("BeneficiarInfo.Size"),
            align: "left",
            format: (value: any) => getFileSize(value),
        },
        {
            id: "addedAt",
            label: t("BeneficiarInfo.AddedAt"),
            align: "left",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "uploadedBy",
            label: "",
            align: "center",
            format: (value, id, row) =>
                value == userEmail && <Basket onClick={() => handleShowDocumentDeleteModal(row)} />,
        },
    ];

    const renderMobileDocumentsRow = (document: any) => {
        return (
            <div className="list-mobile-element">
                <div className="element-left">
                    <div className="element-title-wrapper">
                        <div className="mui-cell">
                            <Pdf />
                            {document.name}
                        </div>
                    </div>
                    <DocumentInfos
                        document={document}
                        documents={documentList}
                        userEmail={userEmail}
                        beneficiarCompagnies={beneficiarCompagnies}
                    />
                </div>
            </div>
        );
    };

    // open document action for mobile
    const onOpenDocumentAction = (document: IDocument) => {
        displayMobileNavBtn(false);
        setActionModal({ open: true, document: document });
    };
    // close document action for mobile
    const onCloseDocumentAction = () => {
        displayMobileNavBtn(true);
        setActionModal(initialActionModalState);
    };
    // delete document action for mobile
    const onDeleteDocument = () => {
        dispatch(deleteDocumentBP({ documentId: actionModal.document?.id ?? -1 }));
    };

    const onOpenSendDocument = () => {
        if (isMobile) displayMobileNavBtn(false);
        setIsModalOpen(true);
    };

    const onCloseSendDocument = () => {
        setIsModalOpen(false);
        setDefaultTypeId(null);
    };

    return (
        <div className="bp-settings-documents" style={isPWAMode ? { paddingBottom: "6.5rem" } : {}}>
            {isMobile && (
                <ActionDocumentModal
                    open={actionModal.open}
                    close={onCloseDocumentAction}
                    documentName={actionModal.document?.name}
                    document={actionModal?.document}
                    downloadDocument={() => handleFileDownload(actionModal.document)}
                    deleteDocument={() => onDeleteDocument()}
                    userEmail={userEmail}
                    renderDocumentInfos={() => (
                        <DocumentInfos
                            document={actionModal.document as IDocument}
                            documents={documentList}
                            userEmail={userEmail}
                            beneficiarCompagnies={beneficiarCompagnies}
                        />
                    )}
                />
            )}
            <div className="bp-settings-header" style={{ paddingBottom: "12px", marginTop: "-4px" }}>
                <h1 style={{ marginBottom: 0 }}>{t("Settings.BPDocumentsTitle")}</h1>
                {tab === "transmitted" && (
                    <ButtonBase
                        variant="outlined"
                        styles={{
                            border: "1px solid #141516",
                            gap: "8px",
                            height: "100%",
                            borderRadius: "4px",
                        }}
                        onClick={() => onOpenSendDocument()}
                    >
                        <SendDocument />
                        {t("AddDocument.SendDocument")}
                    </ButtonBase>
                )}
            </div>
            {/* Outside the tab panels: deposits from the expected documents tab open it too */}
            <AddDocument
                isModalOpen={isModalOpen}
                onClose={onCloseSendDocument}
                onFileAdd={handleFileAdd}
                companies={beneficiarCompagnies}
                pickerCompany
                chosenCompanies={chosenCompanies}
                setChosenCompanies={setChosenCompanies}
                documentTypes={uploadCompanyTypes}
                documents={documentsOfUploadCompany}
                defaultDocumentTypeId={defaultTypeId}
            />
            <TabContext value={tab}>
                <StyledTabs
                    value={tab}
                    onChange={(_, newTab) => setChosenTab(newTab)}
                    sx={{ "& .MuiTab-root:first-of-type": { paddingLeft: 0 } }}
                >
                    {hasExpectedDocuments && <Tab label={t("BeneficiaryDocuments.Portal.Title")} value="expected" />}
                    <Tab label={t("Settings.BPDocumentsTabTransmitted")} value="transmitted" />
                    <Tab label={t("Settings.BPDocumentsTabSigned")} value="signed" />
                </StyledTabs>
                <TabPanel value="expected" sx={{ padding: 0 }}>
                    <ExpectedDocuments
                        entries={expectedDocuments}
                        companies={beneficiarCompagnies}
                        statusRules={statusRules}
                        onDeposit={onDepositExpected}
                    />
                </TabPanel>
                <TabPanel value="transmitted" sx={{ padding: 0 }}>
                    <div className="bp-settings-info-bloc">
                        <div className="bp-settings-documents-filters">
                            <div className="bp-settings-documents-text">
                                <span>{t("Settings.HistoryText")}</span>
                            </div>
                            {beneficiarCompagnies.length > 1 && (
                                <div className="picker-company-wrapper">
                                    <PickerCompany
                                        data={beneficiarCompagnies}
                                        selectedData={filterDocChosenCompanies}
                                        setSelectedData={setFilteredDocChosenCompanies}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="documents">
                            <DeleteModal
                                elementTitle={`"${documentToDelete?.name ?? ""}"`}
                                open={isDeleteModalOpen}
                                onDelete={handleDeleteDocument}
                                onClose={() => handleDeleteDocument(false)}
                            />
                            <LastElementTable
                                isMobile={isMobile}
                                renderMobileRow={renderMobileDocumentsRow}
                                onRowClick={isMobile ? (id, id2, type, row) => onOpenDocumentAction(row) : undefined}
                                columns={documentsTableColumns}
                                data={filteredDocuments}
                                stickyFooter
                                max={-1}
                                dataType="documents"
                            />
                            {filteredDocuments.length === 0 && isMobile && (
                                <div className="documents-documents-empty-state">
                                    {documentListLoadStatus !== "pending" && documentListLoadStatus !== "idle" ? (
                                        <EmptyState type="documents" sx={{ marginTop: "24px" }} />
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </TabPanel>
                <TabPanel value="signed" sx={{ padding: 0 }}>
                    <div className="bp-settings-info-bloc">
                        <BPSignedDocumentsList
                            beneficiarCompagnies={beneficiarCompagnies}
                            headerText={t("Settings.BPDocumentsSignedText")}
                        />
                    </div>
                </TabPanel>
            </TabContext>
        </div>
    );
};

interface IExpectedDocumentsProps {
    entries: IExpectedDocumentsByCompany[];
    companies: IBeneficiariesCompanies[];
    statusRules: ReturnType<typeof documentStatusRules>;
    onDeposit: (companyId: number, expected: IExpectedDocument) => void;
}

/** "Documents attendus": one row per type the publisher expects, with its status and what to do next */
const ExpectedDocuments = ({ entries, companies, statusRules, onDeposit }: IExpectedDocumentsProps) => {
    const [t] = useTranslation();
    const withExpected = entries.filter((entry) => entry.expected.length > 0);

    const detail = (expected: IExpectedDocument) => {
        if (expected.hasPending && expected.status !== "pending")
            return t("BeneficiaryDocuments.Portal.PendingRenewal");
        switch (expected.status) {
            case "valid":
                return expected.validUntil
                    ? t("BeneficiaryDocuments.Portal.ValidUntil", { date: formatShortDate(expected.validUntil) })
                    : t("BeneficiaryDocuments.Portal.NoExpiry");
            case "expiring_soon":
                return t("BeneficiaryDocuments.Portal.ExpiresOn", {
                    date: formatShortDate(expected.validUntil as string),
                });
            case "expired":
                return t("BeneficiaryDocuments.Portal.ExpiredOn", {
                    date: formatShortDate(expected.validUntil as string),
                });
            case "pending":
                return t("BeneficiaryDocuments.Portal.Pending");
            default:
                return expected.rejectionReason
                    ? t("BeneficiaryDocuments.Portal.Rejected", { reason: expected.rejectionReason })
                    : t("BeneficiaryDocuments.Portal.ToSend");
        }
    };

    return (
        <div className="bp-settings-info-bloc bp-expected-documents" id="expected">
            <div className="bp-settings-documents-text">
                <span>{t("BeneficiaryDocuments.Portal.Subtitle")}</span>
            </div>
            {withExpected.map((entry) => (
                <div className="bp-expected-company" key={entry.companyId}>
                    {withExpected.length > 1 && (
                        <div className="documents-table-company-logo bp-expected-company-name">
                            <Avatar
                                variant="rounded"
                                sx={{ width: 20, height: 20 }}
                                src={getBPAvatarUrl(entry.companyId, companies)}
                            />
                            {getBPCompanyName(entry.companyId, companies)}
                        </div>
                    )}
                    {entry.expected.map((expected) => (
                        <div className="bp-expected-row" key={expected.typeId}>
                            <div className="bp-expected-main">
                                <span className="bp-expected-name">{expected.typeName}</span>
                                <span className={`bp-expected-detail${expected.rejectionReason ? " rejected" : ""}`}>
                                    {detail(expected)}
                                </span>
                            </div>
                            {expected.status !== "missing" && (
                                <StatusChip status={expected.status} statusRules={statusRules} />
                            )}
                            {expected.status !== "pending" && !expected.hasPending && (
                                <ButtonBase
                                    variant="outlined"
                                    styles={{ border: "1px solid #141516", gap: "8px", borderRadius: "4px" }}
                                    onClick={() => onDeposit(entry.companyId, expected)}
                                >
                                    <SendDocument />
                                    {t(
                                        expected.status === "valid" || expected.status === "expiring_soon"
                                            ? "BeneficiaryDocuments.Portal.Renew"
                                            : "BeneficiaryDocuments.Portal.Deposit",
                                    )}
                                </ButtonBase>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

/** Tracking status of a history row, with the rejection reason; nothing for untyped legacy documents */
const DocumentStatusChip = ({ document, documents }: { document: IDocument; documents: IDocument[] }) => {
    const [t] = useTranslation();
    const statusRules = useMemo(() => documentStatusRules(t), [t]);
    if (!document.documentTypeId && document.status === "valid") return <span className="bp-documents-muted">—</span>;
    const sameCompany = documents.filter((item) => item.companyId === document.companyId);
    const status = getDocumentRowStatus(document as any, sameCompany);
    const reason = document.rejectionReason ?? "";

    return (
        <StatusChip
            status={status}
            statusRules={statusRules}
            tooltipText={reason}
            extraText={status === "rejected" && reason ? `· ${reason}` : undefined}
        />
    );
};

interface IDocumentInfos {
    document: IDocument;
    documents: IDocument[];
    userEmail: string;
    beneficiarCompagnies: IBeneficiariesCompanies[];
}
const DocumentInfos = ({ document, documents, userEmail, beneficiarCompagnies }: IDocumentInfos) => {
    const [t] = useTranslation();
    return (
        <div className="bp-document-infos">
            {(document.documentTypeId || document.status !== "valid") && (
                <div className="element-subtitle">
                    <DocumentStatusChip document={document} documents={documents} />
                </div>
            )}
            <div className="element-subtitle">
                {`${t("BPStatement.SentOn")} : ${formatLocaleDateString({
                    date: document.addedAt,
                    options: { numeric: true, timeZone: "UTC" },
                })}`}
                {document?.uploadedBy == userEmail && (
                    <span>{`${t("BPStatement.By")} : ${t("BeneficiarInfo.You")}`}</span>
                )}
                {document.uploadedBy !== userEmail && (
                    <div className="documents-table-company-logo">
                        {t("BPStatement.By")}
                        <Avatar
                            variant="rounded"
                            sx={{ width: 14, height: 14 }}
                            src={getBPAvatarUrl(document.companyId, beneficiarCompagnies)}
                        />
                        {getBPCompanyName(document.companyId, beneficiarCompagnies)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BPDocuments;
